import { SubmissionErrors } from 'final-form';
import { FC, useCallback, useEffect } from 'react';
import { Field, Form } from 'react-final-form';
import i18next from 'i18next';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { validatorEmail, validatorRequired } from '~src/services/validators';
import AutoCompleteEmail from '~src/services/autoCompleteEmail';
import LoginButton from '~src/shared/buttons/LoginButton';
import OnChange from '~src/shared/reactFinalForm/OnChange';
import { composeValidators } from '~src/shared/reactFinalForm/validators';
import TosAndDataPrivacyInfoBox from '~src/outer_modules/shared/tosAndDataPrivacyInfoBox/TosAndDataPrivacyInfoBox';
import SvgIcon from '~src/shared/SvgIcon';
import IconInput, { InputIconType } from '~src/shared/form/inputs/IconInput';
import { SuccessContainer } from '~src/outer_modules/shared/shared.sc';
import { FieldType } from '~src/types';
import { FormTitle } from '~src/outer_modules/login/shared.sc';
import { SignupFormValuesI } from '~src/store/session/interfaces';

import {
  FormSubTitle,
  InfoText,
  NameFormContainer,
  SignUpForm,
  SignUpSection,
  SignupSuccessSubTitle,
  SignupSuccessTitle,
  InfoBox,
} from './signupForm.sc';

const trim = (value: string) => value && value.trim();

interface Props {
  t: i18next.TFunction;
  signupLoading: boolean;
  showSignupSuccessMessage: boolean;
  onSubmit(values: SignupFormValuesI): Promise<SubmissionErrors | void>;
}

const SignupForm: FC<Props & RouteComponentProps<any>> = props => {
  const renderField = useCallback(
    ({ autoFocus, input, label, meta: { touched, error }, labelKey }: FieldType) => (
      <IconInput
        {...input}
        autoFocus={autoFocus}
        label={label}
        type={input.type as InputIconType}
        error={touched ? error : null}
        labelKey={labelKey}
      />
    ),
    [],
  );

  const path = props.location.pathname;
  const search = props.location.search;

  // LN-520 : Hotjar integration
  useEffect(() => {
    const okrs = search.indexOf('okrs=1') !== -1;
    const surveys = search.indexOf('surveys=1') !== -1;
    let hjid = 0;
    if (surveys) {
      hjid = 3767716;
    }
    if (okrs && surveys) {
      hjid = 3765121;
    }
    if (!hjid) {
      return;
    }
    // @ts-ignore
    (function (h, o, t, j, a, r) {
      h.hj =
        h.hj ||
        function () {
          (h.hj.q = h.hj.q || []).push(arguments);
        };
      h._hjSettings = { hjid: hjid, hjsv: 6 };
      a = o.getElementsByTagName('head')[0];
      r = o.createElement('script');
      r.async = 1;
      r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
      a.appendChild(r);
    })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
  }, [search, path]);

  const { t, showSignupSuccessMessage = false, signupLoading = false } = props;
  const companyNamePlaceholder = t('x.login.input.placeholder.company_name');
  const emailPlaceholder = t('x.login.input.placeholder.email');
  const firstnamePlaceholder = t('x.login.input.placeholder.firstname');
  const lastnamePlaceholder = t('x.login.input.placeholder.lastname');
  const phonePlaceholder = t('x.login.field.phone.placeholder');
  const autoCompleteEmail = new AutoCompleteEmail();

  return (
    <div>
      {showSignupSuccessMessage ? (
        <div>
          <SuccessContainer data-translation="x.signup.after_signup_info">
            <SvgIcon name="iconOutBox" width="50px" />
            <SignupSuccessTitle data-translation="x.signup.succes_box.title">
              {t('x.signup.succes_box.title')}
            </SignupSuccessTitle>
            <SignupSuccessSubTitle data-translation="x.signup.succes_box.subtitle">
              {t('x.signup.succes_box.subtitle')}
            </SignupSuccessSubTitle>
          </SuccessContainer>
        </div>
      ) : (
        <span>
          <FormTitle data-translation="x.signup.form.header.title">
            {t('x.signup.form.header.title')}
          </FormTitle>
          <FormSubTitle data-translation="x.signup.form.header.subtitle">
            {t('x.signup.form.header.subtitle')}
          </FormSubTitle>
        </span>
      )}
      <SignUpSection>
        {!showSignupSuccessMessage && (
          <Form onSubmit={props.onSubmit}>
            {({ handleSubmit, form }) => {
              const autoComplete = (value: string) => {
                autoCompleteEmail.reset();
                const user = autoCompleteEmail.autoComplete(value);
                form.change('firstname', user.firstname);
                form.change('lastname', user.lastname);
              };

              return (
                <SignUpForm onSubmit={handleSubmit} noValidate={false}>
                  <Field
                    autoFocus
                    name="companyName"
                    label={companyNamePlaceholder}
                    type="company"
                    children={renderField}
                    validate={validatorRequired}
                    labelKey="x.login.input.placeholder.company_name"
                  />
                  <Field
                    name="email"
                    label={emailPlaceholder}
                    type="email"
                    children={renderField}
                    validate={composeValidators([validatorRequired, validatorEmail])}
                    parse={trim}
                    onBlur={autoComplete}
                    labelKey="x.login.input.placeholder.email"
                  />
                  <OnChange name="email">{autoComplete}</OnChange>
                  <NameFormContainer>
                    <Field
                      name="firstname"
                      label={firstnamePlaceholder}
                      type="user"
                      children={renderField}
                      validate={validatorRequired}
                      parse={trim}
                      labelKey="x.login.input.placeholder.firstname"
                    />
                    <Field
                      name="lastname"
                      label={lastnamePlaceholder}
                      type="user"
                      children={renderField}
                      validate={validatorRequired}
                      parse={trim}
                      labelKey="x.login.input.placeholder.lastname"
                    />
                  </NameFormContainer>
                  <Field
                    name="phoneNumber"
                    label={phonePlaceholder}
                    type="phone"
                    children={renderField}
                    validate={validatorRequired}
                    labelKey="x.contact.field.phone.placeholder"
                  />
                  <LoginButton isLoading={signupLoading}>
                    <span data-translation="x.signup.button.signup.text">
                      {t('x.signup.button.signup.text')}
                    </span>
                  </LoginButton>
                </SignUpForm>
              );
            }}
          </Form>
        )}
        {showSignupSuccessMessage ? (
          <InfoBox data-translation="x.signup.succes_box.footer.message">
            <InfoText>{t('x.signup.succes_box.footer.message')}</InfoText>
          </InfoBox>
        ) : (
          <TosAndDataPrivacyInfoBox t={t} showCreditCardNeededBox />
        )}
      </SignUpSection>
    </div>
  );
};

export default withRouter(SignupForm);
